import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { CollectionPaths, StudentDocument, AttendanceStatus } from 'shared_schemas';

interface Props {
    schoolId: string; // Needed for subcollections
    eventId: string;
    eventDate: string; // YYYY-MM-DD
}

interface AttendanceState {
    [studentId: string]: AttendanceStatus;
}

export function AttendanceComp({ schoolId, eventId, eventDate }: Props) {
    const [students, setStudents] = useState<StudentDocument[]>([]);
    const [attendance, setAttendance] = useState<AttendanceState>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [filter, setFilter] = useState('');

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                // 1. Fetch Students (Ideally filtered by roster, but for now grab active ones)
                const studentsRef = collection(db, CollectionPaths.students(schoolId));
                // const studentsQ = query(studentsRef, where('active', '==', true));
                // Temporarily removing active filter to debug visibility
                const studentsQ = query(studentsRef);
                const studentsSnap = await getDocs(studentsQ);
                const studentsData = studentsSnap.docs.map(d => ({ id: d.id, ...d.data() }) as StudentDocument);

                // Sort by last name
                studentsData.sort((a, b) => a.lastName.localeCompare(b.lastName));
                setStudents(studentsData);

                // 2. Fetch Existing Attendance
                const attendanceRef = collection(db, CollectionPaths.attendance(schoolId));

                // Query by date range (start of day to end of day)
                const startOfDay = new Date(eventDate);
                startOfDay.setHours(0, 0, 0, 0);
                const endOfDay = new Date(eventDate);
                endOfDay.setHours(23, 59, 59, 999);

                const attQ = query(attendanceRef, where('date', '>=', startOfDay), where('date', '<=', endOfDay));
                const attSnap = await getDocs(attQ);

                const newAttendance: AttendanceState = {};
                attSnap.docs.forEach(doc => {
                    const data = doc.data() as any;
                    if (data.eventId === eventId) {
                        newAttendance[data.studentId] = data.status;
                    }
                });
                setAttendance(newAttendance);

            } catch (err) {
                console.error('Error loading attendance:', err);
            } finally {
                setLoading(false);
            }
        };

        if (schoolId && eventId) {
            loadData();
        }
    }, [schoolId, eventId, eventDate]);

    const handleStatusChange = (studentId: string, status: AttendanceStatus) => {
        setAttendance(prev => ({
            ...prev,
            [studentId]: status
        }));
    };

    const saveAttendance = async () => {
        setSaving(true);
        try {
            const batch = writeBatch(db);
            const attendanceRef = collection(db, CollectionPaths.attendance(schoolId));

            // Loop through local state
            for (const [studentId, status] of Object.entries(attendance)) {
                // Generate a deterministic ID: eventId_studentId
                const docId = `${eventId}_${studentId}`;
                const docRef = doc(attendanceRef, docId);

                batch.set(docRef, {
                    id: docId,
                    studentId,
                    eventId, // Storing this even if not in schema types yet
                    date: new Date(eventDate),
                    eventType: 'practice', // Default, should come from event
                    status,
                    checkInMethod: 'manual',
                    recordedBy: 'admin', // Placeholder
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                }, { merge: true });
            }

            await batch.commit();
            alert('Attendance saved!');
        } catch (err) {
            console.error('Error saving attendance:', err);
            alert('Failed to save.');
        } finally {
            setSaving(false);
        }
    };

    const filteredStudents = students.filter(s =>
        filter === '' ||
        s.firstName.toLowerCase().includes(filter.toLowerCase()) ||
        s.lastName.toLowerCase().includes(filter.toLowerCase()) ||
        (s.gamerTags && Object.values(s.gamerTags).some(t => t.toLowerCase().includes(filter.toLowerCase())))
    );

    if (loading) return <div className="p-4 text-center">Loading students...</div>;

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <input
                    type="text"
                    placeholder="Search students..."
                    className="input max-w-xs"
                    value={filter}
                    onChange={e => setFilter(e.target.value)}
                />
                <button
                    onClick={saveAttendance}
                    disabled={saving}
                    className="btn-primary"
                >
                    {saving ? 'Saving...' : 'Save Attendance'}
                </button>
            </div>

            <div className="bg-secondary-800 rounded-xl overflow-hidden shadow-lg border border-secondary-700">
                <table className="w-full text-left">
                    <thead className="bg-secondary-900 border-b border-secondary-700">
                        <tr>
                            <th className="p-4 font-semibold text-secondary-300">Student</th>
                            <th className="p-4 font-semibold text-secondary-300">Grade</th>
                            <th className="p-4 font-semibold text-secondary-300 text-center">Present</th>
                            <th className="p-4 font-semibold text-secondary-300 text-center">Late</th>
                            <th className="p-4 font-semibold text-secondary-300 text-center">Absent</th>
                            <th className="p-4 font-semibold text-secondary-300 text-center">Excused</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-secondary-700">
                        {filteredStudents.map(student => {
                            const currentStatus = attendance[student.id];
                            return (
                                <tr key={student.id} className="hover:bg-secondary-700/50 transition-colors">
                                    <td className="p-4">
                                        <div className="font-medium text-white">{student.firstName} {student.lastName}</div>
                                        {student.gamerTags && Object.values(student.gamerTags)[0] && (
                                            <div className="text-xs text-secondary-400">
                                                {Object.values(student.gamerTags)[0]}
                                            </div>
                                        )}
                                    </td>
                                    <td className="p-4 text-secondary-400">{student.grade}</td>

                                    {/* Status Radio Buttons */}
                                    {(['present', 'late', 'absent', 'excused'] as const).map(status => (
                                        <td key={status} className="p-4 text-center">
                                            <label className="cursor-pointer block text-center">
                                                <input
                                                    type="radio"
                                                    name={`status-${student.id}`}
                                                    checked={currentStatus === status}
                                                    onChange={() => handleStatusChange(student.id, status)}
                                                    className={`w-5 h-5 bg-secondary-800 border-secondary-500 focus:ring-primary-500 text-primary-600 
                                                        ${currentStatus === status ? 'accent-primary-500' : ''}`}
                                                />
                                            </label>
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {filteredStudents.length === 0 && (
                    <div className="p-8 text-center text-secondary-400">
                        No students found.
                    </div>
                )}
            </div>
        </div>
    );
}
